import type { SituacaoCaso } from '../lib/api.ts';
import { FILA, SIGNIFICADO_DAS_SITUACOES, SITUACOES, diasAte } from '../lib/rotulos.ts';
import { paraTela } from '../lib/datas.ts';

/**
 * ⭐ **A PELE NOVA, provada na ficha do caso — decisão 20 do estudo, 13/09/2026.**
 *
 * > *"Provar na ficha do caso... Decidir vendo. Uma mudança por vez, vista
 * > funcionando. Tomar muito cuidado para que não venha a deixar inoperante a
 * > aba de relatórios: a mudança precisa ser somente de visual."* — Richard
 *
 * ⛔ **Nada aqui manda em nada.** Os dois desenhos só MOSTRAM o que o cartão já
 * sabe: a situação continua mudando pelo seletor, e o prazo continua escrito
 * em "Entrega". Tirar qualquer um dos dois deixa o cartão exatamente como era.
 */

/**
 * A escada do caso em bolinhas: as etapas feitas cheias, a atual com o ponto
 * de ouro, as seguintes vazias.
 *
 * ⚠️ **Suspenso não é etapa** (`FILA`): o caso suspenso mostra a escada inteira
 * apagada, e o seletor ao lado diz "Suspenso".
 */
export function PercursoDoCaso({ situacao }: { situacao: SituacaoCaso }) {
  const atual = FILA.indexOf(situacao);
  return (
    <ol
      className={'percurso' + (atual < 0 ? ' percurso-suspenso' : '')}
      aria-label="Etapas do caso"
    >
      {FILA.map((s, i) => (
        <li
          key={s}
          className={i < atual ? 'feita' : i === atual ? 'atual' : 'futura'}
          aria-current={i === atual ? 'step' : undefined}
          title={`${SITUACOES[s]}: ${SIGNIFICADO_DAS_SITUACOES[s]}`}
        >
          <span className="percurso-ponto" aria-hidden="true">
            {/* ⭐ Feita leva o traço de conferido; a atual, o ponto de ouro
                (CSS); a seguinte fica vazia — desenho de 14/09/2026. */}
            {i < atual && (
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5 10 17 19 7.5" />
              </svg>
            )}
          </span>
          <span className="percurso-nome">{SITUACOES[s]}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * O prazo numa linha, da assinatura à entrega, com o ponto de HOJE.
 *
 * ⚠️ **Parado e verdadeiro:** sem animação, e o ponto fica onde o dia está.
 * Passada a entrega, a linha inteira fica na cor de atraso.
 *
 * ⛔ **Caso sem assinatura NÃO ganha linha** — continua mostrando a pendência,
 * como sempre: o prazo conta da assinatura, e desenhar uma régua sem começo
 * seria inventar a data que falta. Encerrado também não: prazo que acabou não
 * se acompanha.
 *
 * ⚠️ **`aria-hidden`:** as duas datas e o "faltam N dias" já estão escritos no
 * cartão, e o leitor de tela os lê ali. A linha é só para o olho.
 */
export function PrazoNaLinha({
  assinadoEm,
  prazoEm,
  hoje,
  encerrado,
}: {
  assinadoEm: string | null;
  prazoEm: string | null;
  hoje: string;
  encerrado: boolean;
}) {
  if (!assinadoEm || !prazoEm || !hoje || encerrado) return null;
  const total = diasAte(prazoEm, assinadoEm);
  if (total <= 0) return null;

  const fracao = Math.min(1, Math.max(0, diasAte(hoje, assinadoEm) / total));
  const atrasado = diasAte(prazoEm, hoje) < 0;
  const onde = `${(fracao * 100).toFixed(2)}%`;
  /* Perto das pontas, a palavra HOJE encosta na borda do cartão e passa dela:
     ali ela se alinha para dentro. Visto na prova de 13/09, com caso atrasado. */
  const ponta = fracao > 0.9 ? ' hoje-no-fim' : fracao < 0.1 ? ' hoje-no-inicio' : '';

  return (
    <div className={'prazo-na-linha' + (atrasado ? ' prazo-atrasado' : '') + ponta} aria-hidden="true">
      <div className="prazo-trilho">
        <span className="prazo-feito" style={{ width: onde }} />
        <span className="prazo-hoje" style={{ left: onde }}>
          <span className="prazo-hoje-rotulo">Hoje</span>
        </span>
      </div>
      <div className="prazo-pontas">
        <span className="dado">{paraTela(assinadoEm)}</span>
        <span className="dado">{paraTela(prazoEm)}</span>
      </div>
    </div>
  );
}
